import { PEOPLE_SEARCH_MAX, PEOPLE_SEARCH_MIN, type ProjectPerson } from "@esi/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { type RefObject, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ApiError } from "../../api-errors.ts";
import { useDebounced } from "../../conversation/ConversationList.tsx";
import { projectKeys } from "../queries.ts";

/** What the People tab holds: the text typed, and the person picked from the results. */
export interface PersonField {
  text: string;
  picked: ProjectPerson | null;
}

export const EMPTY_PERSON: PersonField = { text: "", picked: null };

/** The server's own rule for an address (`addProjectMemberRequestSchema`). */
const email = z.email();
const isEmail = (text: string) => email.safeParse(text).success;

/** What adding sends for the field: the picked person's ID, else a typed e-mail address. */
export function personTarget(field: PersonField): { userId: string } | { email: string } | null {
  if (field.picked) {
    return { userId: field.picked.userId };
  }
  const text = field.text.trim();
  return isEmail(text) ? { email: text } : null;
}

/**
 * The People tab's search (spec `project-settings` "Members and group grants from settings"):
 * an ARIA 1.2 combobox over the project's people search. Arrow keys move through the results,
 * Enter picks, Escape closes the list (the dialog only on a second press, via `closeList`).
 * Members are listed but cannot be picked. A pick shows as one row with a way back; nobody
 * found leaves the typed e-mail address to add as before.
 */
export function PeoplePicker(props: {
  value: PersonField;
  onChange: (next: PersonField) => void;
  search: (query: string) => Promise<ProjectPerson[]>;
  /** Scopes the cached results (the project). */
  scope: string;
  /** Closes an open list and says so, so the dialog stays open on that Escape. */
  closeList: RefObject<(() => boolean) | null>;
  disabled: boolean;
  /** After a pick (the input is gone): the dialog moves focus on, to its Add button. */
  onPicked?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const { value, onChange } = props;
  const text = value.text.trim();
  const settled = useDebounced(text);
  // Past the search's longest query, only a typed e-mail address can be added.
  const searching =
    !value.picked && settled.length >= PEOPLE_SEARCH_MIN && settled.length <= PEOPLE_SEARCH_MAX;
  const results = useQuery({
    queryKey: projectKeys.people(props.scope, settled),
    queryFn: () => props.search(settled),
    enabled: searching,
    staleTime: 30_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
  const people =
    searching && text.length >= PEOPLE_SEARCH_MIN && text.length <= PEOPLE_SEARCH_MAX
      ? (results.data ?? [])
      : [];
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(-1);
  const expanded = open && people.length > 0;
  // Results of an earlier query stay on screen while typing, but only the current query's
  // results can be picked: Enter must never pick someone the text no longer names.
  const fresh = settled === text && !results.isPlaceholderData;

  // New results start on the first person who can be picked.
  const data = results.data;
  useEffect(() => {
    setActive(data ? data.findIndex((p) => !p.member) : -1);
  }, [data]);

  const { closeList } = props;
  useEffect(() => {
    closeList.current = () => {
      if (!expanded) {
        return false;
      }
      setOpen(false);
      return true;
    };
    return () => {
      closeList.current = null;
    };
  }, [closeList, expanded]);

  const choose = (person: ProjectPerson | undefined) => {
    if (!person || person.member || !fresh) {
      return;
    }
    // The query stays, so choosing someone else shows the same results again.
    onChange({ text: value.text, picked: person });
    setOpen(false);
    props.onPicked?.();
  };
  const move = (step: 1 | -1) => {
    if (people.length === 0) {
      return;
    }
    setOpen(true);
    let next = active;
    for (let i = 0; i < people.length; i++) {
      next = (next + step + people.length) % people.length;
      if (!people[next]?.member) {
        setActive(next);
        // Focus stays in the input, so the browser does not follow the active option.
        document.getElementById(optionId(next))?.scrollIntoView?.({ block: "nearest" });
        return;
      }
    }
  };

  const optionId = (i: number) => `${id}-option-${i}`;
  const listId = `${id}-list`;
  const statusId = `${id}-status`;

  if (value.picked) {
    const person = value.picked;
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-medium text-fg">{t("settings.members.person.label")}</p>
        <div className="flex items-center gap-3 rounded-lg border border-line bg-surface-raised px-3 py-2">
          <Avatar name={person.displayName ?? person.email} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-fg">
              {person.displayName ?? person.email}
            </p>
            {person.displayName ? (
              <p className="truncate text-xs text-fg-muted">{person.email}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={props.disabled}
            aria-label={t("settings.members.person.clear")}
            onClick={() => {
              onChange({ text: value.text, picked: null });
              setOpen(true);
              requestAnimationFrame(() => input.current?.focus());
            }}
          >
            <X aria-hidden="true" className="size-4" strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    );
  }

  let status: string;
  if (text.length < PEOPLE_SEARCH_MIN) {
    status = t("settings.members.person.hint");
  } else if (text.length > PEOPLE_SEARCH_MAX) {
    status = isEmail(text)
      ? t("settings.members.person.noneEmail")
      : t("settings.members.person.none");
  } else if (results.isError && !results.isFetching) {
    status =
      results.error instanceof ApiError && results.error.status === 429
        ? t("settings.members.person.throttled")
        : t("settings.members.person.failed");
  } else if (!results.data || results.isPlaceholderData || settled !== text) {
    status = t("settings.members.person.searching");
  } else if (people.length === 0) {
    status = isEmail(text)
      ? t("settings.members.person.noneEmail")
      : t("settings.members.person.none");
  } else {
    status = t("settings.members.person.found", { count: people.length });
  }
  // The count is for screen readers; sighted people see the list.
  const quiet = people.length > 0 && fresh;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`${id}-input`}>{t("settings.members.person.label")}</Label>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
          strokeWidth={1.75}
        />
        <Input
          ref={input}
          id={`${id}-input`}
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={expanded && fresh && active >= 0 ? optionId(active) : undefined}
          aria-describedby={statusId}
          autoComplete="off"
          spellCheck={false}
          placeholder={t("settings.members.person.placeholder")}
          className="pl-9"
          disabled={props.disabled}
          value={value.text}
          onChange={(e) => {
            setOpen(true);
            onChange({ text: e.currentTarget.value, picked: null });
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              move(e.key === "ArrowDown" ? 1 : -1);
            } else if (e.key === "Enter" && expanded) {
              // Picks instead of submitting the dialog; results still catching up with the
              // text are not picked from (nor submitted past).
              e.preventDefault();
              if (fresh && active >= 0) {
                choose(people[active]);
              }
            }
          }}
          onBlur={() => setOpen(false)}
          onFocus={() => setOpen(true)}
        />
      </div>
      {expanded ? (
        <div
          id={listId}
          role="listbox"
          aria-label={t("settings.members.person.results")}
          // Scrolling the list (its scrollbar) must not blur the input and close it.
          onMouseDown={(e) => e.preventDefault()}
          className="max-h-72 divide-y divide-line overflow-y-auto rounded-lg border border-line bg-surface-raised"
        >
          {people.map((person, i) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard (aria-activedescendant).
            <div
              key={person.userId}
              id={optionId(i)}
              role="option"
              tabIndex={-1}
              aria-selected={fresh && i === active}
              aria-disabled={person.member || undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-(--duration-fast)",
                person.member ? "cursor-default" : "cursor-pointer",
                fresh && i === active && "bg-surface-hover",
              )}
              // Keeps focus in the input, so the list stays open until the pick lands.
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => {
                if (!person.member && active !== i) {
                  setActive(i);
                }
              }}
              onClick={() => choose(person)}
            >
              <Avatar name={person.displayName ?? person.email} />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "truncate font-medium",
                    person.member ? "text-fg-secondary" : "text-fg",
                  )}
                >
                  <Prefix text={person.displayName ?? person.email} prefix={text} />
                </p>
                {person.displayName ? (
                  <p className="truncate text-xs text-fg-muted">
                    <Prefix text={person.email} prefix={text} />
                  </p>
                ) : null}
              </div>
              {person.member ? (
                <span className="flex shrink-0 items-center gap-1 text-xs text-fg-muted">
                  <Check aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                  {t("settings.members.person.member")}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <p
        id={statusId}
        aria-live="polite"
        className={cn("text-xs text-fg-muted", quiet && "sr-only")}
      >
        {status}
      </p>
    </div>
  );
}

/** The part of `text` the search matched (its start, ignoring case), marked like selected text. */
function Prefix({ text, prefix }: { text: string; prefix: string }) {
  if (!prefix || !text.toLocaleLowerCase().startsWith(prefix.toLocaleLowerCase())) {
    return text;
  }
  return (
    <>
      <mark className="rounded-xs bg-mark px-px text-mark-fg">{text.slice(0, prefix.length)}</mark>
      {text.slice(prefix.length)}
    </>
  );
}
